//libraries
import trackFathom from "components/tracking/trackFathom";
import { cloneElement, isValidElement, useEffect, useRef, useState } from "react";
import { OsTypes, isMobileOnly, osName } from "react-device-detect";
import useReactRouter from "use-react-router";
import "../../css/Website.scss";
import StipplLogoImpact from "../../img/stippl-logo--impact.svg";
import StipplLogo from "../../img/stippl-logo.svg";

import AppStoreButtons from "components/downloadappcta/appstorebuttons/AppStoreButtons";
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import Icon from "components/Icons/Icon";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { useIsSignedIn } from "components/website/components/useIsSignedIn";
import { t } from "i18n/index";
import { buildLocalizedPath, hasLocalisedRoute } from "i18n/urlLocale";
import { useMarketingLang } from "i18n/useMarketingLang";
import Hamburger from "../../img/hamburger.png";
import { getChromeTranslations } from "../../marketing/i18n/pages/chrome";
import DownloadAppModal from "../DownloadAppModal";
import ExploreSearchOverlay from "../ExploreSearchOverlay";
import FeatureMenu from "../FeatureMenu";
import { FeatureMenuItem, getExperienceFeatures, getPlanFeatures, getReliveFeatures } from "../featureMenuItems";

type Props = {
	impact: boolean;
	isLandingPage?: boolean;
	landingPageCTA?: any;
	langMenu?: React.ReactNode;
	/** Keep the dark impact bar after scrolling too (the share page sits under it, it never scrolls away). */
	stayImpact?: boolean;
	/**
	 * The signed-in visitor's face and first name, after Open app (share page, sheet 197 row B). Passed by
	 * the host rather than read here: this component must not import Firebase (see useIsSignedIn).
	 */
	account?: { name: string; picture?: string; initials: string; onLogout: () => void };
};

const Navigation: React.FC<Props> = ({ impact = false, isLandingPage = false, landingPageCTA, langMenu, stayImpact = false, account }) => {
	const { match } = useReactRouter();
	const [scrolled, setScrolled] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	// Default to desktop server-side and on first client render — flip after mount
	// to avoid hydration mismatches (react-device-detect reads navigator.userAgent
	// which doesn't exist on the server).
	const [isMobile, setIsMobile] = useState(false);
	const [isNarrow, setIsNarrow] = useState(false);
	// main's OneLink change (#1121) replaces the local state: every store CTA has to carry the
	// AppsFlyer attribution, and a plain constant loses it. The redesign's own state below stays.
	// 123yb6937h3: useAppDownloadUrl no longer takes a desktop-fallback store URL at all -- it
	// always resolves a OneLink, mobile or desktop. See the hook's doc comment for why.
	const appStoreUrl = useAppDownloadUrl();
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const searchInputRef = useRef<HTMLInputElement>(null);
	// The account pill opens Profile / Log out; a click outside or Esc closes it.
	const [accountOpen, setAccountOpen] = useState(false);
	const accountRef = useRef<HTMLLIElement>(null);
	useEffect(() => {
		if (!accountOpen) return undefined;
		const onDown = (e: MouseEvent) => {
			if (!accountRef.current?.contains(e.target as Node)) setAccountOpen(false);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setAccountOpen(false);
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [accountOpen]);
	// 123yb692rq2: a signed-in visitor was still offered "Login" / "Join for free" here, and
	// clicking either did nothing (LoginModal's own logged-in effect opened and immediately
	// closed the gate). `useIsSignedIn` is the SSR-safe, lazy-firebase check — see its own doc
	// comment for why this can't be a static `useUser`/`components/firebase` import: Navigation
	// renders on every marketing page, so that import would ship Firebase to every visitor and
	// break SSR.
	const isSignedIn = useIsSignedIn();

	const lang = useMarketingLang();
	const translations = getChromeTranslations(lang);
	// 123yb692pu8: the logo and every internal nav link (blog, about, contact, the Plan/
	// Experience/Share&Relive feature links) were un-prefixed, so a /es/ reader clicking any of
	// them landed back on the English page. Reuses `lang` above rather than a second read of the
	// path — see LanguageSwitcher.tsx's doc comment for how that split caused the same class of
	// bug there. `/discover/*` links (none in this component) would need `discoverPath` instead —
	// see its doc comment in `i18n/urlLocale.ts` for why the two lists aren't interchangeable.
	//
	// That fix over-shot at the time: it prefixed every one of these links regardless of whether a
	// locale route actually existed for them, so gating on `hasLocalisedRoute` was added — only
	// paths that resolve to a server-rendered locale page keep the prefix, everything else falls
	// back to the bare English path. The gate itself hasn't changed; what changed underneath it is
	// that the feature-page links (itinerary planner, esim, ...) now DO have a locale route —
	// `noindex` ones, phase 3 step 1 of work/locale-coverage-unification.md — so `hasLocalisedRoute`
	// now says yes for them too and this same gate prefixes them correctly on its own.
	const localizedHref = (path: string) => (hasLocalisedRoute(path) ? buildLocalizedPath(path, lang) : path);

	useEffect(() => {
		setIsMobile(isMobileOnly);
	}, []);

	useEffect(() => {
		const check = () => setIsNarrow(window.innerWidth <= 768);
		check();
		window.addEventListener("resize", check);
		return () => window.removeEventListener("resize", check);
	}, []);

	const handleScroll = () => {
		// react-modal's mobile body-lock (`position: fixed`, see General.scss
		// `.ReactModal__Body--open`) resets `window.scrollY` to 0 for the duration any modal is
		// open, even though `--scroll-lock-offset` (set in planGate.tsx's `usePlanGate`) keeps the
		// page visually in place — that offset trick can't stop `window.scrollY` itself from
		// resetting. Without this guard, that reset fires a `scroll` event here, flips `scrolled`
		// off, and replays this header's own background-color/padding-top transition every time a
		// modal (e.g. TravelWidget's "search hotels" login gate) opens on mobile — a visible color
		// flash with no real scroll behind it.
		if (typeof document !== "undefined" && document.body.classList.contains("ReactModal__Body--open")) return;

		const offset = window.scrollY;

		if (offset > 50) {
			setScrolled(true);
		} else {
			setScrolled(false);
		}
	};

	useEffect(() => {
		if (typeof window === "undefined") return;
		window.addEventListener("scroll", handleScroll);
	});

	const handleAppTracking = () => {
		if (osName === OsTypes.Android) {
			trackFathom("BNQKH0MH");
		} else {
			trackFathom("FD21GVOP");
		}
	};

	const handleAppClick = (e: React.MouseEvent) => {
		e.preventDefault();
		handleAppTracking();
		setShowDownloadModal(true);
	};

	// Nav renders two /register CTAs (desktop topbar + the mobile slide-out) and two /login
	// links (same two places) — one gate and one modal for the whole component, per the
	// 123yb691qz1 rule: any CTA aimed at /register or /login opens the auth modal in place
	// instead of navigating. `path` reuses the `topbar: ${match.path}` label both signup
	// anchors already tracked under, mobile-nav one included, so the property Amplitude/GA see
	// doesn't change even though the mechanism firing it does.
	const gate = usePlanGate(`topbar: ${match.path}`, "store");
	// Which view the shared modal opens on. /register leaves this at the default; /login flips
	// it first. Logging in is not a plan/signup action, so it never calls `gate.requestPlan()` —
	// that would fire `click_signup_cta` and, on a phone, send the visitor to the app store,
	// neither of which is right for someone who already has an account.
	const [authView, setAuthView] = useState<"login" | "register">("register");

	const handleJoinClick = () => {
		// The GA4 "Sign up CTA" event the topbar fired before the gate existed; the gate covers
		// the shared `click_signup_cta` event and the Fathom goal, not this GA-only category.
		// react-ga4 is only ever used inside this handler, so it's loaded on demand rather than
		// shipped in the topbar's critical chunk for a reader who never clicks it.
		import("react-ga4")
			.then(({ default: ReactGA }) => {
				ReactGA.event({
					category: "Sign up CTA",
					action: "click_signup_cta",
					label: `topbar: ${match.path}`,
				});
			})
			.catch((e) => {
				console.error("[Analytics] react-ga4 failed to load:", { error: e?.message || e });
			});
		setAuthView("register");
		gate.requestPlan();
	};

	const handleLoginClick = (e: React.MouseEvent) => {
		// On a phone the /login link keeps navigating to /login — 123yb691bdn's "plan CTAs go
		// to the store on mobile web" rule is about signup, not about someone who already has
		// an account, so this is the one auth anchor that is exempt from the gate on a phone.
		if (gate.isPhone) return;
		e.preventDefault();
		setAuthView("login");
		gate.setAuthOpen(true);
	};

	const renderListItem = (item: FeatureMenuItem) => (
		<li>
			<a href={localizedHref(item.url)} className={`${match?.path === item?.url ? "active" : ""} ${item?.color}`}>
				<span className={`icon-badge icon-badge--${item.color}`}>
					<Icon name={item.icon} fill="currentColor" />
				</span>
				{item.label}
			</a>
		</li>
	);

	const showImpact = impact && (stayImpact || !scrolled);

	return (
		<header className={`navigation ${scrolled && !stayImpact ? "scrolled" : ""} ${showImpact ? "impact" : ""}`}>
			<div className="logo">
				<a href={localizedHref("/")}>
					<img src={showImpact ? StipplLogoImpact : StipplLogo} width={113} height={30} alt="Stippl logo" />
				</a>
			</div>
			{isLandingPage ? (
				<nav>{landingPageCTA}</nav>
			) : !isMobile && !isNarrow && !isLandingPage ? (
				<>
					<button
						type="button"
						className="nav-search"
						data-search-pill
						onClick={() => {
							setSearchOpen(true);
							requestAnimationFrame(() => searchInputRef.current?.focus());
						}}
					>
						<Icon name="search" />
						<span>{t(translations, "nav.searchPlaceholder", "Discover countries & destinations")}</span>
					</button>
					<nav className="center-nav">
						<ul>
							<li className="nav__item">
								<a href="https://business.stippl.io" className="dropdown-menu__label">
									{t(translations, "nav.business", "Business")}
								</a>
							</li>
							<li className="nav__item">
								<a href={localizedHref("/blog")} className="dropdown-menu__label">
									{t(translations, "nav.blog", "Blog")}
								</a>
							</li>
							<li className="nav__item">
								<FeatureMenu label={t(translations, "nav.features", "Features")} translations={translations} />
							</li>
						</ul>
					</nav>
					<nav className="nav-right">
						<ul>
							{isSignedIn ? (
								// Both auth CTAs collapse into one link once we know the visitor is signed
								// in — there is nothing left to log into or join. 123yb692rq2.
								<li className="nav__item">
									<a className="btn-rounded solid big nav-join-btn" href="/home">
										{t(translations, "nav.openApp", "Open app")}
									</a>
								</li>
							) : (
								<li className="nav__item">
									<a href="/login" onClick={handleLoginClick}>{t(translations, "nav.login", "Login")}</a>
								</li>
							)}
							<li className="nav__item">
								<a className="btn-rounded nav-app-btn" href={appStoreUrl} onClick={handleAppClick}>
									<svg width="13" height="18" viewBox="0 0 20.0015 28.0002" fill="none" aria-hidden="true">
										<path
											d="M14.2505 1.5564C14.2505 1.5564 14.0645 2.7044 13.8875 3.3064C13.6595 4.0814 13.1885 4.6464 12.3365 4.8054C10.9465 5.0644 9.0555 5.0644 7.6655 4.8054C6.8125 4.6464 6.3425 4.0814 6.1145 3.3064C5.9475 2.7374 5.6875 1.5734 5.6875 1.5734"
											stroke="currentColor"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeMiterlimit="10"
											strokeWidth="2"
										/>
										<path
											d="M16.1273 26.0405C12.9633 27.2975 7.50225 27.3425 3.87425 26.0405C2.88425 25.6855 2.14325 24.8135 1.93825 23.7725C0.68725 17.3975 0.68725 10.7985 1.93825 4.4245C2.14225 3.3845 2.89025 2.5285 3.87425 2.1565C7.95825 0.6145 12.0433 0.6145 16.1273 2.1565C17.1113 2.5275 17.8582 3.3835 18.0632 4.4245C19.3142 10.7995 19.3142 17.3985 18.0632 23.7725C17.8582 24.8125 17.1043 25.6515 16.1273 26.0405Z"
											stroke="currentColor"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeMiterlimit="10"
											strokeWidth="2"
										/>
									</svg>
									{t(translations, "nav.app", "App")}
								</a>
							</li>
							{!isSignedIn && (
								<li>
									<a className="btn-rounded solid big nav-join-btn" href="/register" onClick={(e) => { e.preventDefault(); handleJoinClick(); }}>
										{t(translations, "nav.joinFree", "Join for free")}
									</a>
								</li>
							)}
							{langMenu && <li className="nav__item">{langMenu}</li>}
							{isSignedIn && account ? (
								<li className="nav-account-menu" ref={accountRef}>
									<button
										type="button"
										className="nav-account"
										id="nav-account"
										data-testid="nav-account"
										aria-haspopup="menu"
										aria-expanded={accountOpen}
										onClick={() => setAccountOpen((open) => !open)}
									>
										{account.picture ? (
											<img className="nav-account__face" src={account.picture} alt="" />
										) : (
											<span className="nav-account__face nav-account__face--initials">{account.initials}</span>
										)}
										<span className="nav-account__name">{account.name}</span>
										<Icon name="chevron-down" size={9} fill="currentColor" />
									</button>
									{accountOpen ? (
										<ul className="nav-account-menu__list" role="menu">
											<li role="none">
												<a role="menuitem" href="/profile" id="nav-account-profile" data-testid="nav-account-profile">
													{t(translations, "nav.profile", "Profile")}
												</a>
											</li>
											<li role="none">
												<button
													type="button"
													role="menuitem"
													id="nav-account-logout"
													data-testid="nav-account-logout"
													onClick={() => {
														setAccountOpen(false);
														account.onLogout();
													}}
												>
													{t(translations, "nav.logout", "Log out")}
												</button>
											</li>
										</ul>
									) : null}
								</li>
							) : null}
						</ul>
					</nav>
				</>
			) : (
				<nav className={`mobile-nav ${mobileMenuOpen && "open"}`}>
					<div className="right">
						{scrolled ? (
							<a className="btn-rounded outlined primary-1 medium" href={appStoreUrl} onClick={handleAppClick}>
								{t(translations, "nav.getTheApp", "Get the app")}
							</a>
						) : null}
						<div className="menu-icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
							<img src={Hamburger} alt="Menu" />
						</div>
					</div>
					<div className="mobile-nav__menu">
						<div className="scroll-area">
							<header>
								<div className="logo">
									<a href={localizedHref("/")}>
										<img src={StipplLogo} width={113} height={30} alt="Stippl logo" />
									</a>
								</div>
								<div className="back-cross" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
									<Icon name="close" />
								</div>
							</header>
							<button
								type="button"
								className="mobile-nav__search"
								data-search-pill
								onClick={() => {
									setMobileMenuOpen(false);
									setSearchOpen(true);
									requestAnimationFrame(() => searchInputRef.current?.focus());
								}}
							>
								<Icon name="search" />
								<span>{t(translations, "nav.searchPlaceholder", "Discover countries & destinations")}</span>
							</button>
							<div className="features-menu">
								<div className="feature-section">
									<h4>{t(translations, "nav.plan", "Plan")}</h4>
									<ul>{getPlanFeatures(translations).map((item: any) => renderListItem(item))}</ul>
								</div>
								<div className="feature-section">
									<h4>{t(translations, "nav.experience", "Experience")}</h4>
									<ul>{getExperienceFeatures(translations).map((item: any) => renderListItem(item))}</ul>
								</div>
								<div className="feature-section">
									<h4>{t(translations, "nav.shareRelive", "Share & Relive")}</h4>
									<ul>{getReliveFeatures(translations).map((item: any) => renderListItem(item))}</ul>
								</div>
							</div>
							<ul className="secondary-menu">
								{langMenu && isValidElement(langMenu) && (
									<li className="secondary-menu__lang">{cloneElement(langMenu as React.ReactElement<any>, { variant: "grid" })}</li>
								)}
								<li>
									<a href="https://business.stippl.io">{t(translations, "nav.business", "Business")}</a>
								</li>
								<li>
									<a href={localizedHref("/blog")}>{t(translations, "nav.blog", "Blog")}</a>
								</li>
								<li>
									<a href={localizedHref("/about")}>{t(translations, "nav.aboutUs", "About us")}</a>
								</li>
								<li>
									<a href={localizedHref("/contact")}>{t(translations, "nav.contact", "Contact")}</a>
								</li>
								{isSignedIn ? (
									// Same collapse as the desktop topbar above — one link, not two dead CTAs.
									<li>
										<a href="/home">{t(translations, "nav.openApp", "Open app")}</a>
									</li>
								) : (
									<>
										<li>
											<a href="/register" onClick={(e) => { e.preventDefault(); handleJoinClick(); }}>
												{t(translations, "nav.signUpFree", "Sign up for free")}
											</a>
										</li>
										<li>
											<a href="/login" onClick={handleLoginClick}>{t(translations, "nav.login", "Login")}</a>
										</li>
									</>
								)}
							</ul>
						</div>
						<footer>
							<AppStoreButtons />
						</footer>
					</div>
				</nav>
			)}
			<DownloadAppModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} location={match.path} />
			<PlanGateModal
				gate={gate}
				title={t(translations, "auth.createAccount", "Create a free account to start planning")}
				loginTitle={t(translations, "auth.loginStippl", "Log in to Stippl")}
				initialView={authView}
			/>
			<ExploreSearchOverlay
				isOpen={searchOpen}
				query={searchQuery}
				onQueryChange={setSearchQuery}
				onClose={() => setSearchOpen(false)}
				inputRef={searchInputRef}
				lang={lang}
			/>
		</header>
	);
};

export default Navigation;
